import { createSearchRequest } from "Hooks/Api/apiSearchRequest";
import { useApiMotorVehicleSearchProducts } from "Hooks/ApiHooks/useApiPrice";
import React, { useContext } from "react";
import alertToast from "Util/ToastifyUtils";
import { Button, Colors, Display, Sizes } from "../Button/Button";
import ButtonAsyncState from "../Button/ButtonAsyncState";
import { ButtonGroup } from "../Button/ButtonGroup";
import Modal from "../Modal/Modal";
import InlineSpinner from "../Spinner/InlineSpinner";
import { MotorVehicleBillingEntityDetail } from "./MotorVehicleBillingEntityDetail";
import { MotorVehicleSearchProductSelector } from "./MotorVehicleProductCard";
import { MotorVehicleSearchForBusinessEntity } from "./MotorVehicleSearchInputs";
import { MotorVehicleSearchPaymentSummaryCard } from "./MotorVehicleSearchPaymentSummaryCard";
import { MotorVehicleDispatchContext, MotorVehicleSearchContext, MvsStage } from "./MotorVehicleSearchReducers";
import { MvsModal } from "./MvsModalComponents";

type MotorVehicleSearchGenerateWorkflowProps = {
	onClose: (success: boolean) => void;
}

const stageTitles: Record<MvsStage, string> = {
	form: "Create a motor vehicle search report",
	review: "Payment Summary",
};

export const MotorVehicleSearchGenerateWorkflow: React.FunctionComponent<MotorVehicleSearchGenerateWorkflowProps> = ({ onClose }) => {
	const { stage } = useContext(MotorVehicleSearchContext);

	return (
		<Modal isOpen label="Generate Motor Vehicle Search" className={`mvs-modal-content mvs-modal-content__${stage}`}>
			<MvsModal.Header title={stageTitles[stage]} onClose={() => onClose(false)} />
			{stage === "form" && <MotorVehicleSearchFormStage onClose={onClose} />}
			{stage === "review" && <MotorVehicleSearchReviewStage onClose={onClose} />}
		</Modal>
	);
};

export const MotorVehicleSearchFormStage: React.FunctionComponent<MotorVehicleSearchGenerateWorkflowProps> = ({ onClose }) => {
	const { stage, data: generateMotorVehicleSearchDetail } = useContext(MotorVehicleSearchContext);
	const dispatch = useContext(MotorVehicleDispatchContext);
	const { isNextValid } = generateMotorVehicleSearchDetail;

	// Load all the initial data for the modal and then pass it down
	const {
		data: reqServiceProducts,
		type: reqServiceProductStatus
	} = useApiMotorVehicleSearchProducts();

	if (reqServiceProductStatus === 'loading' || reqServiceProductStatus === 'error') return <InlineSpinner />
	if (reqServiceProducts?.length === 0) return <MvsModalEmptyView stage={stage} onClose={() => onClose(false)} />;

	return (
		<>
			<div>
				<MotorVehicleSearchProductSelector productOptions={reqServiceProducts} />
				<MvsModal.SectionTitle name="Search Criteria" />
				<MotorVehicleBillingEntityDetail />
				<MotorVehicleSearchForBusinessEntity />
			</div>

			<ButtonGroup className="modal__actions">
				<Button
					display={Display.Outline}
					colors={Colors.Primary}
					sizes={Sizes.Medium}
					onClick={() => onClose(false)}
				>
					Cancel
				</Button>
				<Button
					display={Display.Solid}
					colors={Colors.Primary}
					sizes={Sizes.Medium}
					onClick={() => dispatch({ type: "SetStage", payload: { stage: "review" } })}
					disabled={!isNextValid}
				>
					Continue
				</Button>
			</ButtonGroup>
		</>
	);
};

export const MotorVehicleSearchReviewStage: React.FunctionComponent<MotorVehicleSearchGenerateWorkflowProps> = ({ onClose }) => {
	const { data } = useContext(MotorVehicleSearchContext);
	const dispatch = useContext(MotorVehicleDispatchContext);

	const handleOnPressSearch = async () => {
		try {
			await createSearchRequest({
				businessId: data.businessPaymentMethod!.businessId!,
				searchDetail: data.motorVehicleSearchRequest!,
				servicePriceId: data.product!.id!
			});
			alertToast("Your report is generating, we will send you an email once ready.", "info");
			onClose(true);

		} catch (error) {
			alertToast("Your motor vehicle search failed. Please try again or contact us", "error");
		}
	};

	const isValid = data.isNextValid;

	return (
		<>
			<div className="modal__body">
				{!isValid && (
					<div className="alert alert__warning">
						Please "Go Back" and complete the form before proceeding.
					</div>
				)}
				{isValid && (
					<MotorVehicleSearchPaymentSummaryCard
						businessEntityName={data.businessPaymentMethod!.businessName!}
						organisationId={data.businessPaymentMethod!.organisationId!}
						mvsData={data.motorVehicleSearchRequest!}
						productItem={data.product!}
					/>
				)}
			</div>
			<ButtonGroup className="modal__actions">
				<Button
					display={Display.Outline}
					colors={Colors.Primary}
					sizes={Sizes.Medium}
					onClick={() => dispatch({ type: "SetStage", payload: { stage: "form" } })}
				>
					Back
				</Button>
				<ButtonAsyncState
					display={Display.Solid}
					colors={Colors.Primary}
					sizes={Sizes.Medium}
					onPress={handleOnPressSearch}
					waitingText="Creating Search..."
					readonly={!isValid}
				>
					Confirm & Pay
				</ButtonAsyncState>
			</ButtonGroup>
		</>
	);
};

function MvsModalEmptyView(props: {
	stage: MvsStage,
	onClose: () => void
}) {

	const { onClose, stage } = props;

	return (
		<Modal isOpen label="Generate Motor Vehicle Search" className="mvs-modal-content">
			<>
				<MvsModal.Header title={stageTitles[stage]} onClose={onClose} />
				<MvsModal.ContentEmpty />
			</>
		</Modal>
	)
}